import { useQuery } from '@tanstack/react-query';

interface NominatimAddress {
  city?: string;
  town?: string;
  village?: string;
  hamlet?: string;
  municipality?: string;
  state?: string;
  country?: string;
  country_code?: string;
}

/**
 * Reverse-geocode a lat/lng pair into a human-readable location name
 * using OpenStreetMap's Nominatim API.
 *
 * Returns a short label like "San Francisco, CA" or "Tokyo, Japan".
 * Results are cached per coordinate pair for 24 hours.
 */
export function useLocationName(lat: number | undefined, lng: number | undefined) {
  return useQuery({
    queryKey: ['location-name', lat, lng],
    queryFn: async ({ signal }) => {
      if (lat === undefined || lng === undefined) return null;

      const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=10&addressdetails=1`;

      const res = await fetch(url, {
        signal,
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'BCHLocal/1.0 (https://bchlocal.com)',
        },
      });

      if (!res.ok) return null;

      const data = await res.json() as { address?: NominatimAddress };
      if (!data.address) return null;

      return formatLocation(data.address);
    },
    enabled: lat !== undefined && lng !== undefined,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    gcTime: 7 * 24 * 60 * 60 * 1000, // Keep in cache 7 days
    retry: 1,
  });
}

/** Format a Nominatim address into a short human-readable label. */
function formatLocation(address: NominatimAddress): string {
  const city = address.city || address.town || address.village || address.hamlet || address.municipality;
  const state = address.state;
  const country = address.country;
  const countryCode = address.country_code?.toUpperCase();

  // US-style: "San Francisco, CA"
  if (countryCode === 'US' && city && state) {
    return `${city}, ${stateAbbrev(state)}`;
  }

  // Other countries: "Tokyo, Japan" or "Berlin, Germany"
  if (city && country) {
    return `${city}, ${country}`;
  }

  // Fallback: just state + country, or country alone
  if (state && country) {
    return `${state}, ${country}`;
  }

  return country || 'Unknown location';
}

/** Abbreviate US state names to 2-letter codes. */
function stateAbbrev(state: string): string {
  const abbrevs: Record<string, string> = {
    'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR',
    'California': 'CA', 'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE',
    'Florida': 'FL', 'Georgia': 'GA', 'Hawaii': 'HI', 'Idaho': 'ID',
    'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA', 'Kansas': 'KS',
    'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
    'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS',
    'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV',
    'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
    'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK',
    'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
    'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT',
    'Vermont': 'VT', 'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV',
    'Wisconsin': 'WI', 'Wyoming': 'WY', 'District of Columbia': 'DC',
  };
  return abbrevs[state] ?? state;
}
